import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import {
  formatServerDateTime,
  formatServerTime,
  parseServerDate,
  STATUS_META,
  ERROR_RATE_LIMITED,
} from './outboxFormat';

const isPresent = (value) => value !== undefined && value !== null;

const detailSx = { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.75rem', wordBreak: 'break-all' };

/**
 * Linha "rótulo: valor" do painel expandido.
 */
function DetailItem({ label, value }) {
  return (
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body2" color="text.secondary" sx={detailSx}>{isPresent(value) && value !== '' ? String(value) : '—'}</Typography>
    </Box>
  );
}

/**
 * OutboxRow - Linha da fila de WhatsApp; clique expande o conteúdo e o rastro técnico
 * @param {Object} props
 * @param {Object} props.row - linha de admin/whatsapp/outbox.php
 */
function OutboxRow({ row }) {
  const [expanded, setExpanded] = useState(false);

  const {
    id,
    company_id: companyId,
    company_name: companyName,
    is_test: isTest,
    automation_type: automationType,
    message_kind: messageKind,
    template_key: templateKey,
    reference_type: referenceType,
    reference_id: referenceId,
    priority,
    status,
    scheduled_at: scheduledAt,
    next_attempt_at: nextAttemptAt,
    attempts,
    last_error: lastError,
    phone_masked: phoneMasked,
    message_preview: messagePreview,
    message_length: messageLength,
    endpoint,
    caller,
    sent_at: sentAt,
    created_at: createdAt,
    updated_at: updatedAt,
    locked_by: lockedBy,
    locked_at: lockedAt,
    send_log_id: sendLogId,
  } = row;

  const statusMeta = STATUS_META[status] || { label: status || '—', color: 'default' };
  const isDeferred = status === 'pending' && lastError === ERROR_RATE_LIMITED;

  const nextAttemptDate = parseServerDate(nextAttemptAt);
  const showNextAttempt = Boolean(nextAttemptDate && nextAttemptDate.getTime() > Date.now());

  const mainType = automationType || templateKey || messageKind || '—';
  const priorityLabel = priority === 1 ? 'Massa' : 'Operacional';

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => setExpanded((prev) => !prev)}>
        <TableCell>
          <Typography variant="body2">{formatServerDateTime(scheduledAt)}</Typography>
          {showNextAttempt && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
              próx. tentativa {formatServerTime(nextAttemptAt)}
            </Typography>
          )}
        </TableCell>

        <TableCell onClick={(e) => e.stopPropagation()}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Link component={RouterLink} to={`/admin/companies/${companyId}`} sx={{ fontSize: '0.875rem' }}>
              {companyName || `Empresa #${companyId}`}
            </Link>
            {isTest === 1 && <Chip label="teste" size="small" sx={{ height: 18, fontSize: '0.6875rem' }} />}
          </Box>
          <Typography variant="caption" color="text.disabled">#{companyId}</Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2">{mainType}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            <Typography variant="caption" color="text.disabled">{messageKind || '—'}</Typography>
            <Chip
              label={priorityLabel}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.6875rem' }}
            />
          </Box>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.8125rem' }}>
            {phoneMasked || '—'}
          </Typography>
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip label={statusMeta.label} size="small" color={statusMeta.color} sx={{ height: 22, fontSize: '0.75rem' }} />
            {isDeferred && (
              <Chip label="Adiada" size="small" variant="outlined" color="warning" sx={{ height: 22, fontSize: '0.75rem' }} />
            )}
          </Box>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">{attempts}</Typography>
        </TableCell>

        <TableCell>
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            title={lastError || ''}
            sx={{ maxWidth: 220 }}
          >
            {lastError || '—'}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {referenceType ? `${referenceType} #${isPresent(referenceId) ? referenceId : '—'}` : '—'}
          </Typography>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, border: expanded ? undefined : 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#F8F9FC' }}>
              {messagePreview ? (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mb: 2,
                    p: 1.5,
                    bgcolor: '#0B0F14',
                    color: '#D6E2EF',
                    borderRadius: '8px',
                    fontSize: '0.75rem',
                    fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {messagePreview}
                  {messageLength > messagePreview.length ? `… (${messageLength} caracteres)` : ''}
                </Box>
              ) : (
                <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                  conteúdo removido após envio/cancelamento
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                <DetailItem label="Endpoint" value={endpoint} />
                <DetailItem label="Origem (caller)" value={caller} />
                <DetailItem label="Criada em" value={formatServerDateTime(createdAt)} />
                <DetailItem label="Atualizada em" value={formatServerDateTime(updatedAt)} />
                <DetailItem label="Enviada em" value={sentAt ? formatServerDateTime(sentAt) : '—'} />
                <DetailItem label="Travada por" value={lockedBy} />
                <DetailItem label="Travada em" value={lockedAt ? formatServerDateTime(lockedAt) : '—'} />
                <DetailItem label="Registro de envio" value={sendLogId} />
                <DetailItem label="ID na fila" value={id} />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default OutboxRow;
